import React from "react";
import {
    Button,
    Checkbox,
    Dropdown,
    Form,
    Icon,
    Input,
    Segment,
} from "semantic-ui-react";
import { ReportFilters } from "../../../types/models/Reporting";
import { ORDERS_STATUSES } from "../../../utils/constants";
import { PAYMENT_METHOD_OPTIONS } from "../../../utils/dropdownOptions";

interface UserOption {
    key: string;
    value: string;
    text: string;
}

interface Props {
    filters: ReportFilters;
    advisorOptions: UserOption[];
    installerOptions: UserOption[];
    loading: boolean;
    exporting: boolean;
    onChange: (filters: ReportFilters) => void;
    onApplyRange: (from: string, to: string) => void;
    onApply: () => void;
    onReset: () => void;
    onShowPriority: () => void;
    onExport: () => void;
}

const statusOptions = Object.entries(ORDERS_STATUSES).map(([value, status]) => ({
    key: value,
    value,
    text: status.name,
    label: { color: status.color, empty: true },
}));

const toInputDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface DateRange {
    from: string;
    to: string;
}

type DatePresetKey = "currentMonth" | "last30Days" | "previousMonth" | "last3Months" | "last12Months";

const getDatePresets = (today: Date): Record<DatePresetKey, DateRange> => ({
    currentMonth: {
        from: toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: toInputDate(today),
    },
    last30Days: {
        from: toInputDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)),
        to: toInputDate(today),
    },
    previousMonth: {
        from: toInputDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        to: toInputDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    },
    last3Months: {
        from: toInputDate(new Date(today.getFullYear(), today.getMonth() - 2, 1)),
        to: toInputDate(today),
    },
    last12Months: {
        from: toInputDate(new Date(today.getFullYear(), today.getMonth() - 11, 1)),
        to: toInputDate(today),
    },
});

const DashboardFilters = ({
    filters,
    advisorOptions,
    installerOptions,
    loading,
    exporting,
    onChange,
    onApplyRange,
    onApply,
    onReset,
    onShowPriority,
    onExport,
}: Props) => {
    const datePresets = getDatePresets(new Date());
    const [activeDatePreset, setActiveDatePreset] = React.useState<DatePresetKey | undefined>("currentMonth");

    const update = (field: keyof ReportFilters, value: string | boolean | undefined) => {
        if (field === "from" || field === "to") {
            setActiveDatePreset(undefined);
        }
        onChange({ ...filters, [field]: value || undefined });
    };

    const applyDatePreset = (preset: DatePresetKey) => {
        const range = datePresets[preset];
        setActiveDatePreset(preset);
        onApplyRange(range.from, range.to);
    };

    const datePresetButton = (label: string, preset: DatePresetKey) => {
        const active = activeDatePreset === preset;

        return (
            <Button
                type="button"
                size="mini"
                basic={!active}
                className={active ? "dashboard-filter-preset--active" : undefined}
                aria-pressed={active}
                onClick={() => applyDatePreset(preset)}
            >
                {label}
            </Button>
        );
    };

    return (
        <Segment className="dashboard-filters">
            <div className="dashboard-filters__heading">
                <div>
                    <span className="dashboard-eyebrow">Filtros compartidos</span>
                    <h2>Alcance de la información</h2>
                    <p>El periodo se interpreta según cada vista: creación para cohortes y reporte operativo; fecha del evento para ventas y actividad por rol.</p>
                </div>
                <div className="dashboard-filter-presets" aria-label="Rangos rápidos">
                    {datePresetButton("Este mes", "currentMonth")}
                    {datePresetButton("Últimos 30 días", "last30Days")}
                    {datePresetButton("Mes anterior", "previousMonth")}
                    {datePresetButton("3 meses", "last3Months")}
                    {datePresetButton("12 meses", "last12Months")}
                </div>
            </div>

            <Form onSubmit={onApply}>
                <div className="dashboard-filter-grid">
                    <Form.Field>
                        <label>Periodo desde</label>
                        <Input
                            type="date"
                            value={filters.from || ""}
                            onChange={(_, data) => update("from", String(data.value))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Periodo hasta</label>
                        <Input
                            type="date"
                            value={filters.to || ""}
                            onChange={(_, data) => update("to", String(data.value))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Corte temporal (opcional)</label>
                        <Input
                            type="datetime-local"
                            value={filters.asOf || ""}
                            onChange={(_, data) => update("asOf", String(data.value))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Estado vigente al corte</label>
                        <Dropdown
                            fluid
                            clearable
                            selection
                            placeholder="Todos"
                            options={statusOptions}
                            value={filters.status || ""}
                            onChange={(_, data) => update("status", String(data.value || ""))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Búsqueda de pedido</label>
                        <Input
                            icon="search"
                            placeholder="Factura, cliente, SKU o producto"
                            value={filters.search || ""}
                            onChange={(_, data) => update("search", String(data.value))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Asesor</label>
                        <Dropdown
                            fluid
                            clearable
                            search
                            selection
                            placeholder="Todos"
                            options={advisorOptions}
                            value={filters.advisorId || ""}
                            onChange={(_, data) => update("advisorId", String(data.value || ""))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Instalador</label>
                        <Dropdown
                            fluid
                            clearable
                            search
                            selection
                            placeholder="Todos"
                            options={installerOptions}
                            value={filters.installerId || ""}
                            onChange={(_, data) => update("installerId", String(data.value || ""))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Método de pago</label>
                        <Dropdown
                            fluid
                            clearable
                            selection
                            placeholder="Todos"
                            options={PAYMENT_METHOD_OPTIONS}
                            value={filters.paymentMethod || ""}
                            onChange={(_, data) => update("paymentMethod", String(data.value || ""))}
                        />
                    </Form.Field>
                </div>

                <div className="dashboard-filter-actions">
                    <Checkbox
                        label="Solo pedidos que requieren atención"
                        checked={Boolean(filters.attentionOnly)}
                        onChange={(_, data) => update("attentionOnly", Boolean(data.checked))}
                    />
                    <div>
                        <Button
                            type="button"
                            basic
                            onClick={() => {
                                setActiveDatePreset(undefined);
                                onReset();
                            }}
                            disabled={loading}
                        >
                            Limpiar
                        </Button>
                        <Button type="button" basic color="orange" onClick={onShowPriority} disabled={loading}>
                            <Icon name="warning sign" /> Ver cola priorizada
                        </Button>
                        <Button type="button" secondary onClick={onExport} loading={exporting} disabled={loading || exporting}>
                            <Icon name="file excel outline" /> Exportar operativo
                        </Button>
                        <Button type="submit" color="yellow" loading={loading} disabled={loading}>
                            <Icon name="refresh" /> Actualizar vista
                        </Button>
                    </div>
                </div>
            </Form>
        </Segment>
    );
};

export default DashboardFilters;
